/**
 * LibraryGapBanner — inline banner rendered in the chat thread when the AI
 * acknowledges that the user requested a panel type the library cannot fulfill.
 *
 * TDD §4.8.3. Rendered inside ChatPanel's `panel_proposals` message case when
 * `msg.libraryGap` is present. Placed above the panel proposals list so the
 * user sees the honest acknowledgment before reviewing what the AI could offer.
 *
 * Visual treatment:
 *  - Muted amber / yellow-toned info block — "gap acknowledged" tone, not an error.
 *  - Uses `bg-warning-foreground border-warning/30` (matches ThinDataDetectionMessage's
 *    amber callout block, brand-guide §24). No new color tokens.
 *  - No suggestion chips — the gap banner is informational only. The AI's `introText`
 *    handles the conversational acknowledgment; this banner is the structured affordance.
 *
 * Accessibility:
 *  - `role="status"` — implies aria-live="polite"; mounts into the parent role="log"
 *    container (same pattern as ThinDataDetectionMessage).
 *  - Amber callout block has `role="note"` + `aria-label` for screen readers.
 *
 * Tech debt (DO NOT fix here):
 *  - closestHintId is rendered as a raw string identifier (e.g. "campers_by_grade").
 *    A human-readable label mapping from hintId → display name does not yet exist on
 *    the FE. Track separately; Epic 7.3 is the first time hintId surfaces in the FE.
 *
 * Dashboard-era only — never import this component from workflow-era code paths.
 */

import React from 'react'
import { Info } from 'lucide-react'
import type { LibraryGap } from '@/types/Dashboards.types'

// ─── Props ────────────────────────────────────────────────────────────────────

export interface LibraryGapBannerProps {
  libraryGap: LibraryGap
}

// ─── Component ────────────────────────────────────────────────────────────────

export const LibraryGapBanner: React.FC<LibraryGapBannerProps> = ({ libraryGap }) => {
  const { requestedDescription, closestHintId } = libraryGap

  return (
    <div
      className="mb-3"
      // role="status" implies aria-live="polite". The parent messages container
      // already has role="log" — this inner role="status" gives screen readers
      // a focused announcement point for the gap acknowledgment, matching the
      // pattern used by ThinDataDetectionMessage.
      role="status"
      data-testid="library-gap-banner"
    >
      <div
        className="p-3 bg-warning-foreground border border-warning/30 rounded-xl"
        role="note"
        aria-label="Panel library gap acknowledged"
      >
        <div className="flex items-center gap-2 mb-2">
          <Info size={14} className="text-warning flex-shrink-0" aria-hidden="true" data-testid="library-gap-icon" />
          <p className="text-xs font-bold text-warning">
            Panel not available in the library
          </p>
        </div>

        {closestHintId != null ? (
          <p className="text-xs text-warning/80 leading-relaxed">
            I don&apos;t have a panel for{' '}
            <strong>{requestedDescription}</strong>{' '}
            in the library yet. The closest match is{' '}
            <strong>{closestHintId}</strong>.
          </p>
        ) : (
          <p className="text-xs text-warning/80 leading-relaxed">
            I don&apos;t have a panel for{' '}
            <strong>{requestedDescription}</strong>{' '}
            in the library yet, and nothing close enough to suggest.
          </p>
        )}
      </div>
    </div>
  )
}
